import {
  getSearchByIdWhereObject,
  hasKeys,
  IExtendableDto,
  ITypeormWhereCriteria,
  parseSpecificSearchStringToArray,
} from "@ryde/shared";

/**
 * The server-side `where` for an entity whose grid filters client-side but scopes dates on the server.
 *
 * It must never return an empty object: that asks the server for every row the entity owns, which on
 * notifications is the ~120k-row fetch RYDE-4816 exists to remove. The dates are therefore given up only
 * for the two cases that need rows from outside the window, each named explicitly here rather than read
 * from a shared flag - an `id:` search and starred-with-filters-ignored. Both narrow on `id`, the same
 * field the client-side search then filters the rows by (`table.helpers.ts`), so the swap fetches less.
 */
export const buildClientSideDatesServerWhere = ({
  dateQueryObject,
  isStarredFilterIgnoredMode,
  searchText,
  starredIdsWhere,
}: {
  dateQueryObject: ITypeormWhereCriteria<IExtendableDto>;
  isStarredFilterIgnoredMode: boolean;
  searchText: string | undefined;
  starredIdsWhere: ITypeormWhereCriteria<IExtendableDto>;
}): ITypeormWhereCriteria<IExtendableDto> => {
  if (isStarredFilterIgnoredMode) {
    // this mode shows the starred rows and nothing else, so an empty set matches nothing - it does not
    // mean "no filter", which is what an empty `where` would tell the server
    const starredWhereObject: ITypeormWhereCriteria<IExtendableDto> = hasKeys(
      starredIdsWhere,
    )
      ? starredIdsWhere
      : getSearchByIdWhereObject({
          ids: [],
          returnNoResultsOnEmptyArray: true,
        });

    return starredWhereObject;
  }

  const idsFromSearchBox = parseSpecificSearchStringToArray(
    searchText ?? "",
  ).filter(Boolean);

  if (idsFromSearchBox.length) {
    const searchByIdWhereObject: ITypeormWhereCriteria<IExtendableDto> =
      getSearchByIdWhereObject({ ids: idsFromSearchBox });

    return searchByIdWhereObject;
  }

  return dateQueryObject;
};
